import { c as _c } from "react/compiler-runtime";
import React from 'react';
import cx from 'classnames';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import IconShipment from 'components/icons/iconShipment';
import IconInHomeDelivery from 'components/icons/iconInHomeDelivery';
import withLoginPage from 'components/consumer/consumer-login/login-page/hoc/withLoginPage';
import useScreen from 'hooks/useScreen';
import useUserData from 'data-hooks/useUserData';
import SanitizedHtml from 'components/consumer/SanitizedHtml';

// Caravel's 7 statuses onto the 3 milestones. 1/2/3 map to themselves, so this
// still holds if it ever sends only 3.
const LEGACY_STATUS_MAP = {
  1: 1,
  2: 1,
  3: 1,
  4: 1,
  5: 2,
  6: 2,
  7: 3
};
const VERTICAL_STATUSES = [{
  status: 1,
  title: 'Ordered on'
}, {
  status: 2,
  title: 'Shipped'
}, {
  status: 3,
  title: 'Delivered'
}];
const FIRST_STATUS = 1;
const FINAL_STATUS = 3;
const GET_ORDER_TRACKING_DETAILS = gql`
  query getOrderTrackingDetails($order_id: String!) {
    getOrderTrackingDetails(order_id: $order_id) {
      vertical
      delivery_text
      display_delivery_date
      mainDesc
      progress
      order_date
      errors {
        code
        message
      }
    }
  }
`;

// transform variantsText xml format to string
const transformVariantsText = variantsText => variantsText.replace(/<dd>|<dt>/g, '').split('</dd>').filter(str => str?.includes('Fabric')).join(' ').split('</dt>')[1];
const OrderTrackingPage = t0 => {
  const $ = _c(35);
  const {
    orderId
  } = t0;
  const {
    isMobile
  } = useScreen();
  const {
    isLoggedIn
  } = useUserData();
  const t1 = !isLoggedIn || !orderId;
  let t2;
  if ($[0] !== orderId) {
    t2 = orderId?.toString();
    $[0] = orderId;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = {
      order_id: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t3) {
    t4 = {
      skip: t1,
      fetchPolicy: "network-only",
      variables: t3
    };
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    loading,
    data
  } = useSuspenseQuery(GET_ORDER_TRACKING_DETAILS, t4);
  if (loading || !isLoggedIn) {
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="p-8 mx-auto max-w-max">loading...</div>;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    return t5;
  }
  let t10;
  let t11;
  let t12;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== data?.getOrderTrackingDetails || $[9] !== isMobile || $[10] !== orderId) {
    t12 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const {
        vertical,
        delivery_text,
        display_delivery_date,
        mainDesc,
        progress,
        order_date,
        errors
      } = data?.getOrderTrackingDetails || {};
      const itemStatus = _temp;
      const items = Array.isArray(vertical) ? vertical.filter(Boolean) : [];
      const verticalStatus = items.length ? Math.max(...items.map(itemStatus)) : FIRST_STATUS;
      const isDelivered = progress === 100 && items.length > 0 && items.every(v_0 => itemStatus(v_0) === FINAL_STATUS);
      if (errors?.length > 0) {
        t12 = <div className="text-lg font-bold text-center pt-16 h-[calc(100vh-340px)]">{get(errors, "0.message") || "Unable to load tracking details"}</div>;
        break bb0;
      }
      t8 = "p-4 md:p-8 mx-auto max-w-max";
      let t13;
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = <IconShipment />;
        $[19] = t13;
      } else {
        t13 = $[19];
      }
      if ($[20] !== orderId) {
        t9 = <div className="flex flex-row items-center">{t13}<div className="px-2 text-brand text-base font-sans font-bold">Track Order #: J-{orderId}</div></div>;
        $[20] = orderId;
        $[21] = t9;
      } else {
        t9 = $[21];
      }
      t10 = <div className="flex flex-col md:flex-row justify-between items-center pb-6 md:pb-0"><div className="flex-1">{delivery_text && <div className="mt-4 text-gray text-3xl"><div className="font-bold font-sans">{delivery_text}</div><div className="font-bold font-sans">{display_delivery_date}</div></div>}{mainDesc && <SanitizedHtml className="mt-4 text-lg font-bold" html={mainDesc} />}</div></div>;
      if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = <div className="w-full h-px my-8 border border-gray-light6" />;
        $[22] = t11;
      } else {
        t11 = $[22];
      }
      t7 = "mx-auto my-0 mt-4";
      t5 = "grid grid-cols-12 text-gray-50";
      t6 = items.length > 0 && VERTICAL_STATUSES.map(item => {
        const cart = items.filter(v_1 => itemStatus(v_1) === item.status);
        const colDesc = <div className={cx(!isMobile && "col-start-1 col-end-3", "mr-10 md:mx-auto relative w-[200px]", item.status === FINAL_STATUS ? "flex flex-col justify-end items-start mt-4" : item.status !== 1 ? "mt-8" : null)}>{item.status === 1 ? order_date && <div className="text-xl font-bold font-sans mb-2 md:mb-0">Ordered on {order_date}</div> : <div className="text-xl font-sans mb-1">{item.title}</div>}</div>;
        return <div key={item.title} className="contents">{!isMobile && colDesc}<div className={cx("md:mr-10 md:mx-auto relative", isMobile ? "col-start-1 col-end-2" : "col-start-3 col-end-4")}><div className="h-full w-6 flex flex-col items-center justify-center">{item.status === 1 ? <div className={cx("h-full w-1", item.status < verticalStatus ? "bg-brand" : "bg-gray-light7")} /> : item.status === FINAL_STATUS ? <div className={cx("h-full w-1", item.status === verticalStatus ? "bg-brand" : "bg-gray-light7")} /> : <><div className={cx("h-[32px] w-1", item.status <= verticalStatus ? "bg-brand" : "bg-gray-light7")} /><div className={cx("h-[calc(100%_-_32px)] w-1", item.status < verticalStatus ? "bg-brand" : "bg-gray-light7")} /></>}</div><div className={cx("w-6 h-6 absolute rounded-full text-center", item.status === 1 ? "top-0" : item.status === FINAL_STATUS ? "bottom-0" : "top-[32px]", item.status === FINAL_STATUS && isDelivered || item.status < verticalStatus ? "bg-brand border-4 border-white" : item.status === verticalStatus ? "bg-white border-4 border-brand" : item.status > verticalStatus ? "bg-gray-light7 border-4 border-white" : null)} /></div><div className={cx(isMobile ? "col-start-2 col-end-12" : "col-start-4 col-end-12", "px-4 mr-auto w-full", item.status === FINAL_STATUS ? "pt-4" : item.status === 1 ? "mb-4" : null)}>{isMobile && colDesc}{item.status === 1 && order_date && <div className="mb-2"><span className="bg-brand-lightest px-4 py-1 text-brand-darker text-sm font-sans w-auto">Completed {order_date}</span></div>}{item.status === FINAL_STATUS && isDelivered && display_delivery_date && <div className="mb-2 mt-4 md:mt-0"><span className="bg-brand-light px-4 py-2 text-sm mb-6">Completed {display_delivery_date}</span>{cart.some(_temp2) && <div className="flex flex-row text-center mt-4"><IconInHomeDelivery width={24} height={24} /> <span className="text-sm">In-Home Delivery</span></div>}</div>}<div className="flex flex-col md:flex-row md:flex-wrap">{cart?.length > 0 && cart.map(_temp3)}</div></div></div>;
      });
    }
    $[8] = data?.getOrderTrackingDetails;
    $[9] = isMobile;
    $[10] = orderId;
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t10 = $[11];
    t11 = $[12];
    t12 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  if (t12 !== Symbol.for("react.early_return_sentinel")) {
    return t12;
  }
  let t13;
  if ($[23] !== t5 || $[24] !== t6) {
    t13 = <div className={t5}>{t6}</div>;
    $[23] = t5;
    $[24] = t6;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13 || $[27] !== t7) {
    t14 = <div className={t7}>{t13}</div>;
    $[26] = t13;
    $[27] = t7;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t14 || $[32] !== t8 || $[33] !== t9) {
    t15 = <div className={t8}>{t9}{t10}{t11}{t14}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t14;
    $[32] = t8;
    $[33] = t9;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
};
OrderTrackingPage.propTypes = {
  orderId: PropTypes.string
};
export default withLoginPage(OrderTrackingPage);
function _temp(v) {
  const status = parseInt(v?.status, 10);
  return LEGACY_STATUS_MAP[status] || FIRST_STATUS;
}
function _temp2(c) {
  return c.order_cart?.delivery_method === "inhome_delivery";
}
function _temp3(c_0) {
  const fabricText = typeof c_0.order_cart?.variants_text === "string" ? transformVariantsText(c_0.order_cart.variants_text) : undefined;
  const quantity = c_0.order_cart?.quantity;
  return <div key={`${c_0.fk_cart_id}-${c_0.status}`} className="bg-white shadow-md rounded-xl flex flex-row items-start p-4 mr-4 mt-4 md:max-w-[48%]"><div className="flex flex-col"><div className="text-base font-bold font-sans mb-1">{c_0.product?.name}</div>{(fabricText || quantity != null) && <div className="text-sm font-sans text-light2 mb-0.5">{fabricText}{fabricText && quantity != null && <span className="inline-block w-1.5 h-1.5 bg-gray rounded-full mx-2 relative top-[-2px]" />}{quantity != null && `Qty ${quantity}`}</div>}{parseInt(c_0.status, 10) !== FIRST_STATUS && typeof c_0.status_title === "string" && <div className="text-sm font-sans text-brand mt-1">{c_0.status_title}</div>}</div></div>;
}